import React from 'react';

import { enumToOptions } from '../../../../helpers/enumHelper';

import { TemplatePage } from '../../../../components/common/pager';
import TemplateValidatedAutocomplete from '../../../../components/template/custom/validatedAutocomplete';
import { TemplateDatePicker } from '../../../../components/template/templateDatePicker';
import { TemplateImageUpload } from '../../../../components/template/templateImageUpload';
import { TemplateInput } from '../../../../components/template/templateInput';
import { TemplateTextArea } from '../../../../components/template/templateTextArea';
import { TemplateDropdown } from '../../../../components/template/templateDropdown';
import { TemplateCheckbox } from '../../../../components/template/templateCheckbox';
import { TemplateCurrencyTargets } from '../components/templateCurrencyTargets';
import { TemplateFeatureCountries } from '../components/templateFeatureCountries';
import { TemplateToggle } from '../../../../components/template/templateToggle';
import { SystemOnlyGroup } from '../../../../components/common/SystemOnlyBadge';
import { programFeature } from '../../helpers/featureCountries';

import {
  entityTypes,
  imageFormats,
  uploadTypes
} from '../../../../constants/imageUpload';
import { paths } from '../constants/template';
import config from '../../../../constants';

import {
  defaultAmbassadorStatus,
  pageCreationStatus,
  Flow,
} from '../../constants';

export const getSetupTab = (props) => {
  const {
    onChange,
    required,
    programNameValidation,
    participantNameValidation,
    getEventRequest,
    getRecordRequest,
    clearRecord,
    getEventItem,
    onEventChange,
    dateValidation,
    optional,
    currencyTargetsValidation,
    lockedCurrencies,
    requestValidation,
    isSystemAdmin,
    savedGuaranteeCountries,
    savedRegistrationFeeCountries
  } = props;

  return (
    <TemplatePage id="setup" i18nKey="programs.template.page.setup.title">
      <TemplateInput
        id="setupVipProgramName"
        path={paths.PROGRAM_NAME}
        label="programs.template.page.setup.vip-program-name.label"
        onChange={onChange}
        validation={programNameValidation}
      />

      <TemplateValidatedAutocomplete
        id="setupVipProgramEvents"
        valueEntity="page/event"
        entity="program"
        valuePath={paths.EVENT_ID}
        path={paths.EVENT}
        recordPath="eventAmbassadorProgramRequestRecord"
        selected="event"
        label="programs.template.page.setup.event-name.label"
        request={getEventRequest}
        getItem={getEventItem}
        onChange={onEventChange}
        getRecordRequest={getRecordRequest}
        clearRecord={clearRecord}
        validation={requestValidation}
      />

      <TemplateDatePicker
        id="setupVipProgramApplicationsOpenDate"
        path={paths.DATE_APPLICATIONS_OPEN}
        label="programs.template.page.setup.applications-open-date.label"
        subtext="programs.template.page.setup.applications-open-date.subtext"
        onChange={onChange}
        validation={dateValidation}
      />

      <TemplateDatePicker
        id="setupVipProgramApplicationsCloseDate"
        path={paths.DATE_APPLICATIONS_CLOSE}
        label="programs.template.page.setup.applications-close-date.label"
        onChange={onChange}
        validation={dateValidation}
      />

      <TemplateDatePicker
        id="setupVipProgramDateClaimedClose"
        path={paths.DATE_CLAIMS_CLOSE}
        label="programs.template.page.setup.date-claimed-close.label"
        subtext="programs.template.page.setup.date-claimed-close.subtext"
        onChange={onChange}
        validation={dateValidation}
      />

      <TemplateDatePicker
        id="setupVipProgramFundraisingDeadline"
        path={paths.FUNDRAISING_DEADLINE_DATE}
        label="programs.template.page.setup.fundraising-deadline-date.label"
        onChange={onChange}
        validation={dateValidation}
      />
      
      <TemplateCurrencyTargets
        id="setupMinimumFundraisingTarget"
        path={paths.CURRENCY_TARGETS}
        eventCurrencyPath={paths.CURRENCY}
        mirrorPath={paths.FUNDRAISER_MINIMUM_TARGET_AMOUNT}
        lockedCurrencies={lockedCurrencies}
        validation={currencyTargetsValidation}
        label="programs.template.page.setup.currency-targets.label"
        subtext="programs.template.page.setup.currency-targets.subtext"
        onChange={onChange}
      />

      <TemplateCheckbox
        id="enableFundraisingFieldsOverride"
        path={paths.FUNDRAISING_FIELDS_OVERRIDE}
        inputLabel="programs.template.page.setup.campaign-fields-override.label"
        onChange={onChange}
      />

      <TemplateInput
        id="setupVipProgramTerminologyName"
        path={paths.TERMINOLOGY}
        label="programs.template.page.setup.terminology-name.label"
        subtextPosition="top"
        subtext="programs.template.page.setup.terminology-name.sub-text"
        onChange={onChange}
        validation={participantNameValidation}
      />

      <TemplateImageUpload
        id="setupVipProgramHeroImage"
        path={paths.BANNER}
        entity={entityTypes.EVENT}
        uploadType={uploadTypes.HERO_IMAGE}
        position="top"
        height={500}
        width={1500}
        label="programs.template.page.setup.hero-image.label"
        validation={required}
        onChange={onChange}
      />

      <TemplateTextArea
        id="setupVipProgramDescription"
        path={paths.INTRO_TEXT}
        label="programs.template.page.setup.intro-text.label"
        subtext="programs.template.page.setup.intro-text.subtext"
        validation={optional}
        onChange={onChange}
        maxLength={config.VIP_PROGRAM_INTRO_TEXT_MAX_LENGTH}
      />

      {isSystemAdmin && (
        <SystemOnlyGroup>
          <TemplateCheckbox
            id="enableFundraisingGuarantee"
            path={paths.FUNDRAISING_GUARANTEE_ENABLED}
            inputLabel="programs.template.page.setup.fundraising-guarantee.label"
            onChange={onChange}
          />

          <TemplateToggle path={paths.FUNDRAISING_GUARANTEE_ENABLED}>
            <TemplateFeatureCountries
              id="fundraisingGuaranteeCountries"
              path={paths.FUNDRAISING_GUARANTEE_COUNTRIES}
              charityCountriesPath={paths.CHARITY_COUNTRIES}
              feature={programFeature.fundraisingGuarantee}
              savedCountries={savedGuaranteeCountries}
              label="programs.template.page.setup.fundraising-guarantee.countries"
              onChange={onChange}
            />
          </TemplateToggle>

          <TemplateCheckbox
            id="enableRegistrationFee"
            path={paths.REGISTRATION_FEE_ENABLED}
            inputLabel="programs.template.page.setup.registration-fee.label"
            onChange={onChange}
          />

          <TemplateToggle path={paths.REGISTRATION_FEE_ENABLED}>
            <TemplateFeatureCountries
              id="registrationFeeCountries"
              path={paths.REGISTRATION_FEE_COUNTRIES}
              charityCountriesPath={paths.CHARITY_COUNTRIES}
              feature={programFeature.registrationFee}
              savedCountries={savedRegistrationFeeCountries}
              label="programs.template.page.setup.registration-fee.countries"
              onChange={onChange}
            />
          </TemplateToggle>
        </SystemOnlyGroup>
      )}

      <TemplateDropdown
        path={paths.DEFAULT_AMBASSADOR_STATUS}
        label="programs.template.page.setup.default-ambassador-status.label"
        onChange={onChange}
        options={enumToOptions(
          defaultAmbassadorStatus,
          "programs.template.page.setup.default-ambassador-status"
        )}
        clearable
      />

      <TemplateDropdown
        path={paths.PAGE_CREATION_STATUS}
        label="programs.template.page.setup.page-creation-status.label"
        subtext="programs.template.page.setup.page-creation-status.subtext"
        onChange={onChange}
        options={enumToOptions(
          pageCreationStatus,
          "programs.template.page.setup.page-creation-status"
        )}
      />

      <TemplateDropdown
        path={paths.FLOW}
        label="programs.template.page.setup.flow.label"
        onChange={onChange}
        options={enumToOptions(
          Flow,
          "programs.template.page.setup.flow"
        )}
      />
    </TemplatePage>
  );
};
